<template>
  <v-container
    fluid
    class="d-flex justify-center align-center"
    style="height: 100%"
  >
    <v-card
      class="d-flex flex-column justify-center align-center"
      min-width="95%"
      elevation="0"
    >
      <v-dialog v-model="dialog" persistent max-width="700px">
        <v-card>
          <v-card-title>{{
            $t("history.principalHistory.editProject")
          }}</v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="currentProject.name"
                    :rules="nameRules"
                    :label="$t('history.principalHistory.headers.name')"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="currentProject.address"
                    :rules="addressRules"
                    :label="$t('projects.newProjectBasicInformation.address')"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="currentProject.address_line"
                    :label="
                      $t('projects.newProjectBasicInformation.addressLine2')
                    "
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="currentProject.city"
                    :rules="cityRules"
                    :label="$t('projects.newProjectBasicInformation.city')"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="currentProject.state"
                    :rules="stateRules"
                    :label="$t('projects.newProjectBasicInformation.state')"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="currentProject.zip_code"
                    :rules="zipRules"
                    :label="$t('projects.newProjectBasicInformation.zipCode')"
                    outlined
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
            <v-alert v-if="error" type="error" dense text>
              {{ $t("history.principalHistory.errorOccurred") }}
            </v-alert>
            <v-alert v-if="success" type="success" dense text>
              {{ $t("history.principalHistory.projectUpdated") }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="savingProject"
              color="blue darken-1"
              text
              @click="dialog = false"
            >
              {{ $t("history.principalHistory.close") }}
            </v-btn>
            <v-btn
              :disabled="!valid || savingProject"
              color="blue darken-1"
              text
              @click="saveProject"
            >
              {{ $t("history.principalHistory.save") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-card-title style="width: 100%" class="px-10 py-7">
        <v-row>
          <v-col
            class="d-flex justify-start align-center"
            cols="12"
            sm="6"
            md="4"
          >
            <v-select
              v-model="selectedFilters"
              :items="filterOptions"
              :label="$t('history.principalHistory.filters')"
              prepend-inner-icon="mdi-filter-variant"
              multiple
              small-chips
              deletable-chips
              clearable
              filled
              dense
              outlined
              hide-details
              style="width: 100%"
            ></v-select>
          </v-col>
          <v-col class="ml-auto" cols="12" sm="6" md="4">
            <v-text-field
              class="d-flex justify-center align-center"
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('history.principalHistory.search')"
              filled
              dense
              outlined
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-title>
      <v-data-table
        style="width: 100%"
        height="75vh"
        :loading="loading"
        :headers="translatedHeaders"
        :items="filteredProjects"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        hide-default-footer
        mobile-breakpoint="768"
        @page-count="pageCount = $event"
      >
        <template v-slot:[`item.LOR`]="{ item }">
          <span :style="{ color: getColorLor(item.LOR) }">{{
            $t(`history.elements.forms.${item.LOR}`)
          }}</span>
        </template>
        <template v-slot:[`item.intake`]="{ item }">
          <v-chip :color="getColor(item)" :text-color="getTextColor(item)" dark>
            {{
              $t(
                `history.elements.config.intakeOptions.${item.intake.toLowerCase()}`,
              )
            }}
          </v-chip>
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <span :style="{ color: getColorStatus(item.status) }">
            {{ translateStatus(item.status) }}
          </span>
        </template>
        <template v-slot:[`item.project_scope`]="{ item }">
          <div class="scope-container-images">
            <v-img
              v-if="item.project_scope.scope"
              class="scope-images"
              src="@/assets/projectfullview/scope/house_on.svg"
            ></v-img>
            <v-img
              v-else
              class="scope-images"
              src="@/assets/projectfullview/scope/house_off.svg"
            ></v-img>
            <v-img
              v-if="item.project_scope.files"
              class="scope-images"
              src="@/assets/projectfullview/scope/folder_on.svg"
            ></v-img>
            <v-img
              v-else
              class="scope-images"
              src="@/assets/projectfullview/scope/folder_off.svg"
            ></v-img>
            <v-img
              v-if="item.project_scope.documents"
              class="scope-images"
              src="@/assets/projectfullview/scope/note_on.svg"
            ></v-img>
            <v-img
              v-else
              class="scope-images"
              src="@/assets/projectfullview/scope/note_off.svg"
            ></v-img>
          </div>

          <p style="display: none">{{ item.project_scope }}</p>
        </template>
        <template v-slot:[`item.id`]="{ item }">
          <div class="d-flex justify-center align-center">
            <v-btn class="edit-button mr-2" @click="openEditDialog(item)">
              {{ $t("history.principalHistory.editProject") }}
            </v-btn>
            <v-badge
              :content="item.unseen"
              :value="item.unseen"
              :color="item.intake == 'Approved' ? 'green' : '#2b58e2'"
              overlap
              bordered
            >
              <v-btn
                class="edit-button"
                @click="
                  $router.push({
                    name: 'HistoryFullView',
                    params: { id: item.id },
                  })
                "
              >
                {{ $t("history.principalHistory.fullView") }}
              </v-btn>
            </v-badge>
          </div>
        </template>
      </v-data-table>
      <div class="text-center py-5">
        <v-pagination
          total-visible="5"
          v-model="page"
          :length="pageCount"
        ></v-pagination>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import * as Sentry from "@sentry/vue";
import history from "@/api/history";
import project from "@/api/project";
import moment from "moment";
export default {
  name: "History",
  data() {
    return {
      loading: false,
      dialog: false,
      currentProject: {},
      nameRules: [
        (v) =>
          !!v || this.$t("history.principalHistory.validation.name.required"),
        (v) =>
          (v && v.length >= 5) ||
          this.$t("history.principalHistory.validation.name.minLength"),
      ],
      addressRules: [
        (v) =>
          !!v || this.$t("projects.newProjectBasicInformation.addressRequired"),
        (v) =>
          (v && v.length >= 5) ||
          this.$t("projects.newProjectBasicInformation.addressMinLength"),
      ],
      cityRules: [
        (v) =>
          !!v || this.$t("projects.newProjectBasicInformation.cityRequired"),
        (v) =>
          (v && v.length >= 4) ||
          this.$t("projects.newProjectBasicInformation.cityMinLength"),
      ],
      stateRules: [
        (v) =>
          !!v || this.$t("projects.newProjectBasicInformation.stateRequired"),
        (v) =>
          (v && v.length >= 2) ||
          this.$t("projects.newProjectBasicInformation.stateMinLength"),
      ],
      zipRules: [
        (v) =>
          !!v || this.$t("projects.newProjectBasicInformation.zipRequired"),
        (v) =>
          /^\d+$/.test(v) ||
          this.$t("projects.newProjectBasicInformation.zipMustBeNumber"),
      ],
      error: false,
      success: false,
      valid: true,
      savingProject: false,
      selectedFilters: [],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      search: "",
      headers: [
        { text: "SUB#", value: "number", width: "10%" },
        { text: "Name", value: "name", width: "10%" },
        { text: "LOR", value: "LOR", width: "10%" },
        { text: "Intake", value: "intake", width: "10%" },
        { text: "Status", value: "status", width: "10%" },
        { text: "Address", value: "address", width: "15%" },
        { text: "Created Date", value: "created_at", width: "15%" },
        { text: "Company", value: "company_name", width: "10%" },
        { text: "", value: "id", width: "10%", sortable: false },
      ],
      projects: [],
    };
  },
  computed: {
    filteredProjects() {
      const searchLower = this.search.toLowerCase();
      const filtered = this.projects.filter((item) => {
        return (
          item.name.toLowerCase().includes(searchLower) ||
          item.address.toLowerCase().includes(searchLower) ||
          item.created_at.toLowerCase().includes(searchLower) ||
          this.$t(`history.elements.forms.${item.LOR}`)
            .toLowerCase()
            .includes(searchLower) ||
          this.$t(
            `history.elements.config.intakeOptions.${item.intake.toLowerCase()}`,
          )
            .toLowerCase()
            .includes(searchLower) ||
          this.translateStatus(item.status)
            .toLowerCase()
            .includes(searchLower) ||
          item.company_name.toLowerCase().includes(searchLower)
        );
      });

      const valuesOf = (group) =>
        this.selectedFilters
          .filter((filter) => filter.startsWith(`${group}:`))
          .map((filter) => filter.slice(group.length + 1));
      const intakes = valuesOf("intake");
      const statuses = valuesOf("status");
      const onlyNotifications = this.selectedFilters.includes("notifications");

      return filtered.filter(
        (project) =>
          (!intakes.length || intakes.includes(project.intake)) &&
          (!statuses.length || statuses.includes(project.status)) &&
          (!onlyNotifications || project.unseen > 0),
      );
    },
    filterOptions() {
      const t = (key) => this.$t(`history.principalHistory.${key}`);
      return [
        { header: t("headers.intake") },
        { text: t("pending"), value: "intake:Pending" },
        { text: t("rejected"), value: "intake:Rejected" },
        { text: t("incomplete"), value: "intake:Incomplete" },
        { divider: true },
        { header: t("headers.status") },
        { text: t("status"), value: "status:Open" },
        { text: t("negotiation"), value: "status:Negotiations" },
        { text: t("appraisal"), value: "status:Appraisal" },
        { text: t("depreciation"), value: "status:Depreciation" },
        { text: t("appraisalOnly"), value: "status:Appraisal Only" },
        { text: t("attorney"), value: "status:Attorney" },
        { divider: true },
        { header: t("otherFilters") },
        { text: t("pendingNotifications"), value: "notifications" },
      ];
    },
    translatedHeaders() {
      return this.headers.map((header) => ({
        ...header,
        text:
          header.value !== "id"
            ? this.$t(`history.principalHistory.headers.${header.value}`)
            : "",
      }));
    },
  },
  methods: {
    statusTranslationKey(status) {
      return String(status || "")
        .toLowerCase()
        .replace(/\s+/g, "_");
    },
    translateStatus(status) {
      return this.$t(
        `history.elements.config.statusOptions.${this.statusTranslationKey(status)}`,
      );
    },
    async getProjects() {
      this.loading = true;
      try {
        const companyId = this.$route.query.company_id ?? "";
        const response = await history.fetchHistory(companyId);
        if (response.code == 200) {
          return response.data.history.map((project) => ({
            ...project,
            created_at: moment(project.created_at).format("MM / DD / YYYY"),
          }));
        } else {
          return [];
        }
      } catch (error) {
        Sentry.captureException(error);
        return [];
      } finally {
        this.loading = false;
      }
    },
    getColor(item) {
      if (item.intake === "Approved") {
        return "#C9F0D3";
      } else if (item.intake === "Rejected") {
        return "#F8D7DA";
      } else if (item.intake === "Incomplete") {
        return "#ffeecc";
      } else {
        return "#F0F0F0";
      }
    },
    getTextColor(item) {
      if (item.intake === "Approved") {
        return "#399652";
      } else if (item.intake === "Rejected") {
        return "#DD5555";
      } else if (item.intake === "Incomplete") {
        return "#ffc142";
      } else {
        return "#000";
      }
    },
    getColorLor(value) {
      if (value === "Signed") {
        return "#2a58e2";
      } else {
        return "#DD5555";
      }
    },
    getColorStatus(value) {
      if (value === "Open") {
        return "#2a58e2";
      } else if (value === "Closed") {
        return "#DD5555";
      }
    },
    openEditDialog(project) {
      this.error = false;
      this.success = false;
      this.valid = true;
      this.currentProject = { ...project };
      this.dialog = true;
    },
    async saveProject() {
      if (this.$refs.form.validate()) {
        this.success = false;
        this.error = false;
        this.savingProject = true;

        try {
          const data = {
            name: this.currentProject.name,
            address: this.currentProject.address,
            address_line: this.currentProject.address_line,
            city: this.currentProject.city,
            state: this.currentProject.state,
            zip_code: this.currentProject.zip_code,
            project_id: this.currentProject.id,
          };
          const response = await project.updateProject(data);
          if (response.code == 200) {
            this.projects = await this.getProjects();
            this.success = true;
            setTimeout(() => {
              this.success = false;
              this.savingProject = false;
              this.dialog = false;
            }, 3000);
          } else {
            this.error = true;
            this.savingProject = false;
          }
        } catch (error) {
          Sentry.captureException(error);
          this.error = true;
          this.savingProject = false;
        }
      }
    },
  },
  async activated() {
    this.projects = await this.getProjects();
    const dashboardFilters = {
      1: "intake:Pending",
      2: "intake:Rejected",
      4: "status:Open",
    };
    const status = dashboardFilters[this.$route.query.status];
    if (status) {
      this.selectedFilters = [status];
    }
  },
  watch: {
    "$i18n.locale"() {
      this.headers = this.translatedHeaders;
    },
  },
};
</script>

<style scoped>
::v-deep table tbody tr td {
  padding: 20px 16px !important;
}

@media (min-width: 768px) {
  ::v-deep table tbody tr td:first-child,
  ::v-deep table thead tr th:first-child {
    padding-left: 3rem !important;
  }
  ::v-deep table tbody tr td:last-child,
  ::v-deep table thead tr th:last-child {
    padding-right: 3rem !important;
  }

  ::v-deep table tbody tr td {
    border: none !important;
  }
}

::v-deep
  .theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > thead
  > tr:last-child
  > th {
  border-top: thin solid rgba(0, 0, 0, 0.12);
}

::v-deep table thead tr th {
  font-size: 14px !important;
  font-weight: 500 !important;
}

.edit-button {
  background-color: #2b58e2 !important;
  color: white !important;
  text-transform: none;
  font-family: sans-serif !important;
  height: 30px !important;
}

.scope-container-images {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 70px;
}

.scope-images {
  width: 18px;
  height: 18px;
}
</style>
